import { Injectable, inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { exhaustMap, withLatestFrom, map, catchError, of } from "rxjs";
import { CreditCheckClient, QuoteClient } from "src/api-service";
import { selectSelectedCompany } from "../../../user/user.selectors";
import { AppError, normalizeError } from "../../../app-store-error.types";
import { CreditCheckActions } from "./credit-check.actions";

// **************************************************************************
// ERROR MESSAGE MAPPING
// **************************************************************************

/**
 * Pick the toast that says what actually went wrong.
 *
 * The failure toast only ever shows `translate.instant(toastKey)` — the server's own message
 * is never rendered — so an unmapped failure reaches the user as the generic "credit check
 * failed". That is what bug #6979 was: a malformed organisationsnummer on the customer card
 * produced a failure with nothing in it to act on.
 *
 * The codes come from InvalidRegistrationNumberException in the API. Keep them in step.
 * A missing number is fixed where the customer is kept: the customer card, or Attio for subscriptions.
 */
function toastKeyForFailure(
    error: AppError,
    fallbackKey: string,
    missingRegistrationNoKey = "SUBSCRIPTION.TOAST.CREDIT_CHECK_MISSING_REGISTRATION_NO",
): string {
    if (error.status === 404) return "SUBSCRIPTION.TOAST.CREDIT_CHECK_NOT_FOUND";

    switch (error.code) {
        case "INVALID_REGISTRATION_NUMBER":
            return "SUBSCRIPTION.TOAST.CREDIT_CHECK_INVALID_REGISTRATION_NO";
        case "MISSING_REGISTRATION_NUMBER":
            return missingRegistrationNoKey;
        default:
            return fallbackKey;
    }
}

@Injectable()
export class CreditCheckEffects {

    // =========================================================================
    // DEPENDENCY INJECTION
    // =========================================================================
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly creditCheckClient = inject(CreditCheckClient);
    private readonly quoteClient = inject(QuoteClient);

    // *************************************************************************
    // SUBSCRIPTION FLOW
    // *************************************************************************

    runSubscriptionCheck$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CreditCheckActions.runSubscriptionCheck),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            exhaustMap(([{ request, successToastKey, errorToastKey }, selectedCompany]) =>
                this.creditCheckClient.performCreditCheck(selectedCompany.id, request).pipe(
                    map(response => CreditCheckActions.runSubscriptionCheckSuccess({
                        response,
                        toastKey: successToastKey,
                    })),
                    catchError(error => {
                        const normalized = normalizeError(error);
                        return of(CreditCheckActions.runSubscriptionCheckFailure({
                            error: normalized,
                            toastKey: toastKeyForFailure(normalized, errorToastKey, "SUBSCRIPTION.TOAST.CREDIT_CHECK_MISSING_REGISTRATION_NO_ATTIO"),
                        }));
                    }),
                ),
            ),
        ),
    );

    // *************************************************************************
    // QUOTE FLOW
    // *************************************************************************

    runQuoteCheck$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CreditCheckActions.runQuoteCheck),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            exhaustMap(([{ quoteId, versionId }, selectedCompany]) =>
                this.quoteClient.runCreditCheck(quoteId, selectedCompany.id, { versionId }).pipe(
                    map(quote => CreditCheckActions.runQuoteCheckSuccess({ quote })),
                    catchError(error => of(CreditCheckActions.runQuoteCheckFailure({
                        error: normalizeError(error),
                        toastKey: "CREDIT_CHECK.TOAST.RUN_QUOTE_CHECK_ERROR",
                    }))),
                ),
            ),
        ),
    );

    overrideQuoteCheck$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CreditCheckActions.overrideQuoteCheck),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            exhaustMap(([{ quoteId }, selectedCompany]) =>
                this.quoteClient.overrideCreditCheck(quoteId, selectedCompany.id).pipe(
                    map(quote => CreditCheckActions.overrideQuoteCheckSuccess({
                        quote,
                        toastKey: "QUOTE.TOAST.CREDIT_CHECK_OVERRIDE_SUCCESS",
                    })),
                    catchError(error => of(CreditCheckActions.overrideQuoteCheckFailure({
                        error: normalizeError(error),
                        toastKey: "QUOTE.TOAST.CREDIT_CHECK_OVERRIDE_ERROR",
                    }))),
                ),
            ),
        ),
    );
}
